import React, { useState } from "react";
import { useLazyQuery, gql } from "@apollo/client";

const PREVIEW_AUDIENCE = gql`
  query previewCampaignAudience($audienceRules: JSON!) {
    previewCampaignAudience(audienceRules: $audienceRules) {
      count
      error
    }
  }
`;


const INITIAL_FORM = {
  name: "",
  campaignType: "",
  campaignCategory: "",
  triggerType: "MANUAL",
  communicationChannel: "WHATSAPP",
  status: "DRAFT",
  priority: 1,
  cooldownHours: 24,
  attributionWindowHours: 168,
  batchSize: "",
  batchIntervalHours: "",
  // Audience rules — structured
  audience_phone: "",
  audience_minOrders: "",
  audience_maxOrders: "",
  audience_minAmount: "",
  audience_maxAmount: "",
  audience_inactiveSinceDays: "",
  audience_inactiveSinceDaysAnyStatus: "",
  audience_lastActiveDays: "",
  audience_daysBeforeBirthday: "",
  audience_verifiedInLastHours: "",
  // Metadata — structured
  meta_triggerEvent: "",
  meta_cronSchedule: "",
};

const Label = ({ children, sub }) => (
  <div className="mb-1">
    <label className="block text-sm font-medium text-gray-700">{children}</label>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const Input = (props) => (
  <input
    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
    {...props}
  />
);

const Select = ({ children, ...props }) => (
  <select
    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
    {...props}
  >
    {children}
  </select>
);

const SectionHeader = ({ icon, title, desc }) => (
  <div className="flex items-start gap-2 mb-3">
    <span className="text-lg">{icon}</span>
    <div>
      <p className="text-sm font-semibold text-gray-800">{title}</p>
      {desc && <p className="text-xs text-gray-400">{desc}</p>}
    </div>
  </div>
);

/** Convert structured audience fields to audienceRules JSON */
function buildAudienceRules(form) {
  const rules = {};
  if (form.audience_phone) rules.phone = form.audience_phone.trim();
  if (form.audience_minOrders !== "") rules.minOrders = parseInt(form.audience_minOrders);
  if (form.audience_maxOrders !== "") rules.maxOrders = parseInt(form.audience_maxOrders);
  if (form.audience_minAmount !== "") rules.minAmount = parseInt(form.audience_minAmount);
  if (form.audience_maxAmount !== "") rules.maxAmount = parseInt(form.audience_maxAmount);
  if (form.audience_inactiveSinceDays !== "") rules.inactiveSinceDays = parseInt(form.audience_inactiveSinceDays);
  if (form.audience_inactiveSinceDaysAnyStatus !== "") rules.inactiveSinceDaysAnyStatus = parseInt(form.audience_inactiveSinceDaysAnyStatus);
  if (form.audience_lastActiveDays !== "") rules.lastActiveDays = parseInt(form.audience_lastActiveDays);
  if (form.audience_daysBeforeBirthday !== "") rules.daysBeforeBirthday = parseInt(form.audience_daysBeforeBirthday);
  if (form.audience_verifiedInLastHours !== "") rules.verifiedInLastHours = parseInt(form.audience_verifiedInLastHours);
  return Object.keys(rules).length > 0 ? rules : null;
}

function buildMetadata(form) {
  const meta = {};
  if (form.meta_cronSchedule) meta.cronSchedule = form.meta_cronSchedule;
  if (form.meta_triggerEvent) meta.triggerEvent = form.meta_triggerEvent;
  return Object.keys(meta).length > 0 ? meta : null;
}

/** When editing, hydrate form from existing audienceRules + metadata JSON */
function hydrateFromData(initialData) {
  const base = { ...INITIAL_FORM, ...initialData };
  const ar = initialData?.audienceRules || {};
  const meta = initialData?.metadata || {};
  return {
    ...base,
    campaignCategory: initialData?.campaignCategory || "",
    audience_phone: ar.phone || "",
    audience_minOrders: ar.minOrders !== undefined ? String(ar.minOrders) : "",
    audience_maxOrders: ar.maxOrders !== undefined ? String(ar.maxOrders) : "",
    audience_minAmount: ar.minAmount !== undefined ? String(ar.minAmount) : "",
    audience_maxAmount: ar.maxAmount !== undefined ? String(ar.maxAmount) : "",
    audience_inactiveSinceDays: ar.inactiveSinceDays !== undefined ? String(ar.inactiveSinceDays) : "",
    audience_inactiveSinceDaysAnyStatus: ar.inactiveSinceDaysAnyStatus !== undefined ? String(ar.inactiveSinceDaysAnyStatus) : "",
    audience_lastActiveDays: ar.lastActiveDays !== undefined ? String(ar.lastActiveDays) : "",
    audience_daysBeforeBirthday: ar.daysBeforeBirthday !== undefined ? String(ar.daysBeforeBirthday) : "",
    audience_verifiedInLastHours: ar.verifiedInLastHours !== undefined ? String(ar.verifiedInLastHours) : "",
    meta_triggerEvent: meta.triggerEvent || "",
    meta_cronSchedule: meta.cronSchedule || "",
  };
}

const TRIGGER_EVENTS = [
  { value: "", label: "— Select event —" },
  { value: "CART_ABANDONED", label: "Cart Item Added (Abandonment Watch)" },
  { value: "USER_SIGNED_UP", label: "User Signed Up" },
  { value: "FIRST_BOOKING_COMPLETED", label: "First Booking Completed" },
  { value: "BOOKING_COMPLETED", label: "Booking Completed (Post-Appointment Review Request)" },
  { value: "FEEDBACK_FLAGGED", label: "Negative Feedback Flagged (Rating ≤2)" },
  { value: "BOOKING_CANCELLED", label: "Booking Cancelled" },
  { value: "CUSTOMER_NO_SHOW", label: "Customer No-Show (admin-tagged decline)" },
  { value: "BOOKING_REMINDER_DUE", label: "Booking Reminder Window (24h before appointment)" },
  { value: "PAYMENT_FAILED", label: "Payment Failed" },
  { value: "PAYMENT_LINK_SENT", label: "Payment Link Sent" },
  { value: "USER_INACTIVE", label: "User Inactive" },
];

const CAMPAIGN_TYPES = [
  { value: "", label: "— Select type —", category: "" },
  
  // LIFECYCLE
  { value: "LIFECYCLE_NEW_USER", label: "Lifecycle → New User", category: "LIFECYCLE" },
  { value: "LIFECYCLE_FIRST_BOOKING", label: "Lifecycle → First Booking", category: "LIFECYCLE" },
  { value: "LIFECYCLE_REPEAT", label: "Lifecycle → Repeat Customer", category: "LIFECYCLE" },
  { value: "LIFECYCLE_INACTIVE", label: "Lifecycle → Inactive User", category: "LIFECYCLE" },
  { value: "LIFECYCLE_VIP", label: "Lifecycle → VIP Customer", category: "LIFECYCLE" },
  
  // BEHAVIORAL
  { value: "BOOKING_CART_ABANDON", label: "Booking → Cart Abandonment", category: "BEHAVIORAL" },
  { value: "BOOKING_PAYMENT_FAIL", label: "Booking → Payment Failure", category: "BEHAVIORAL" },
  { value: "BOOKING_CANCELLED", label: "Booking → Cancelled", category: "BEHAVIORAL" },
  { value: "BOOKING_REVIEW_REQUEST", label: "Booking → Review Request", category: "BEHAVIORAL" },
  
  // EVENT
  { value: "OCCASION_BIRTHDAY", label: "Occasion → Birthday", category: "EVENT" },
  { value: "OCCASION_ANNIVERSARY", label: "Occasion → Anniversary", category: "EVENT" },
  { value: "OCCASION_FESTIVAL", label: "Occasion → Festival", category: "EVENT" },
  
  // MARKETING
  { value: "MARKETING_OFFER", label: "Marketing → Offer", category: "MARKETING" },
  { value: "MARKETING_REFERRAL", label: "Marketing → Referral", category: "MARKETING" },
  { value: "MARKETING_LOYALTY", label: "Marketing → Loyalty", category: "MARKETING" },
  { value: "SERVICE_REMINDER_HAIRCUT", label: "Service Reminder → Haircut", category: "MARKETING" },
  { value: "SERVICE_REMINDER_FACIAL", label: "Service Reminder → Facial", category: "MARKETING" },
  { value: "SERVICE_REMINDER_WAXING", label: "Service Reminder → Waxing", category: "MARKETING" },
  { value: "SERVICE_REMINDER_HAIR_SPA", label: "Service Reminder → Hair Spa", category: "MARKETING" },
  { value: "WINBACK", label: "Win-back", category: "MARKETING" },
  
  // TRANSACTIONAL
  { value: "TRANSACTIONAL_BOOKING_CONFIRMED", label: "Transactional → Booking Confirmed", category: "TRANSACTIONAL" },
  { value: "TRANSACTIONAL_OTP", label: "Transactional → OTP", category: "TRANSACTIONAL" },
];

const CreateCampaignModal = ({ onClose, onSubmit, initialData }) => {
  const [form, setForm] = useState(
    initialData ? hydrateFromData(initialData) : INITIAL_FORM
  );
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);

  const [getAudiencePreview, { loading: previewLoading, data: previewData }] = useLazyQuery(PREVIEW_AUDIENCE, {
    fetchPolicy: "no-cache",
  });


  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: null });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Campaign name is required";
    if (!form.campaignType.trim()) errs.campaignType = "Campaign type is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: form.name.trim(),
      campaignType: form.campaignType.trim(),
      campaignCategory: form.campaignCategory || null,
      triggerType: form.triggerType,
      communicationChannel: form.communicationChannel,
      status: form.status,
      priority: parseInt(form.priority) || 1,
      cooldownHours: parseInt(form.cooldownHours) || 24,
      attributionWindowHours: parseInt(form.attributionWindowHours) || 168,
      batchSize: form.batchSize ? parseInt(form.batchSize) : null,
      batchIntervalHours: form.batchIntervalHours ? parseInt(form.batchIntervalHours) : null,
      audienceRules: buildAudienceRules(form),
      incentiveType: null,
      incentiveConfig: null,
      metadata: buildMetadata(form),
    };

    if (initialData?.id) payload.id = initialData.id;
    onSubmit(payload);
  };

  const audiencePreview = buildAudienceRules(form);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            {initialData ? "Edit Campaign" : "Create Campaign"}
          </h2>
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`w-2 h-2 rounded-full transition ${step >= s ? "bg-blue-600" : "bg-gray-200"}`}
                />
              ))}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">

            {/* ─── STEP 1: Basic Info ─── */}
            {step === 1 && (
              <>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-700">
                  <strong>Step 1 of 3:</strong> Basic campaign identity and trigger configuration.
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Campaign Name <span className="text-red-500">*</span></Label>
                    <Input name="name" value={form.name} onChange={handleChange} placeholder="e.g. Winback — Inactive 30 Days" />
                    {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                  </div>
                  <div className="col-span-2">
                    <Label sub="Controls priority and mutual exclusion rules">Campaign Category <span className="text-red-500">*</span></Label>
                    <Select name="campaignCategory" value={form.campaignCategory} onChange={handleChange}>
                      <option value="">— Select category —</option>
                      <option value="TRANSACTIONAL">🔔 Transactional (P1 — Always sent, no cap)</option>
                      <option value="BEHAVIORAL">⚡ Behavioral (P2 — Cart abandon, payment fail)</option>
                      <option value="LIFECYCLE">🔄 Lifecycle (P3 — Mutually exclusive per user)</option>
                      <option value="EVENT">🎉 Event (P4 — Birthday, festival, anniversary)</option>
                      <option value="MARKETING">📣 Marketing (P5 — Offers, new services, referral)</option>
                    </Select>
                    {form.campaignCategory === "LIFECYCLE" && (
                      <p className="text-xs text-blue-600 mt-1 bg-blue-50 rounded px-2 py-1">
                        ℹ️ Only one Lifecycle campaign can be active per user at a time.
                      </p>
                    )}
                    {form.campaignCategory === "TRANSACTIONAL" && (
                      <p className="text-xs text-green-600 mt-1 bg-green-50 rounded px-2 py-1">
                        ✅ Transactional campaigns bypass all frequency caps.
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Label>Campaign Type <span className="text-red-500">*</span></Label>
                    <Select name="campaignType" value={form.campaignType} onChange={handleChange} disabled={!form.campaignCategory}>
                      {!form.campaignCategory && <option value="">Select a category first</option>}
                      {CAMPAIGN_TYPES.filter(t => t.value === "" || t.category === form.campaignCategory).map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </Select>
                    {errors.campaignType && <p className="text-xs text-red-500 mt-1">{errors.campaignType}</p>}
                  </div>

                  <div>
                    <Label>Initial Status</Label>
                    <Select name="status" value={form.status} onChange={handleChange}>
                      <option value="DRAFT">Draft</option>
                      <option value="ACTIVE">Active</option>
                      <option value="PAUSED">Paused</option>
                    </Select>
                  </div>
                  <div>
                    <Label>Trigger Type</Label>
                    <Select name="triggerType" value={form.triggerType} onChange={handleChange}>
                      <option value="MANUAL">Manual (Admin runs it)</option>
                      <option value="CRON_SCHEDULED">Cron Scheduled (Auto)</option>
                      <option value="EVENT_DRIVEN">Event Driven (Hooks)</option>
                    </Select>
                  </div>
                  <div>
                    <Label sub="Frequency Cap: Minimum hours to wait before sending this exact campaign to the same user again (prevents spam)">Cooldown Hours</Label>
                    <Input name="cooldownHours" type="number" value={form.cooldownHours} onChange={handleChange} placeholder="24" />
                  </div>
                </div>
              </>
            )}

            {/* ─── STEP 2: Audience ─── */}
            {step === 2 && (
              <>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-700">
                  <strong>Step 2 of 3:</strong> Define who receives this campaign. Leave fields blank to skip that filter.
                </div>

                {/* Target Specific User */}
                <div className="border rounded-lg p-4 space-y-4">
                  <SectionHeader icon="🎯" title="Target Specific User" desc="Target a specific user by their phone number" />
                  <div>
                    <Label>Phone Number</Label>
                    <Input name="audience_phone" type="text" value={form.audience_phone} onChange={handleChange} placeholder="e.g. 9999999999" />
                  </div>
                </div>

                {/* Order History */}
                <div className="border rounded-lg p-4 space-y-4">
                  <SectionHeader icon="📦" title="Order History" desc="Filter by number of verified bookings" />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Min Orders</Label>
                      <Input name="audience_minOrders" type="number" min="0" value={form.audience_minOrders} onChange={handleChange} placeholder="e.g. 1" />
                    </div>
                    <div>
                      <Label>Max Orders</Label>
                      <Input name="audience_maxOrders" type="number" min="0" value={form.audience_maxOrders} onChange={handleChange} placeholder="e.g. 5" />
                    </div>
                    <div>
                      <Label>Min Total Spend (₹)</Label>
                      <Input name="audience_minAmount" type="number" min="0" value={form.audience_minAmount} onChange={handleChange} placeholder="e.g. 500" />
                    </div>
                    <div>
                      <Label>Max Total Spend (₹)</Label>
                      <Input name="audience_maxAmount" type="number" min="0" value={form.audience_maxAmount} onChange={handleChange} placeholder="e.g. 5000" />
                    </div>
                  </div>
                </div>

                {/* Activity Window */}
                <div className="border rounded-lg p-4 space-y-4">
                  <SectionHeader icon="⏱️" title="Activity Window" desc="Target users based on when they last booked" />
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label sub="Users with NO VERIFIED booking in last N days (win-back)">Inactive Since (Verified Only)</Label>
                      <Input name="audience_inactiveSinceDays" type="number" min="0" value={form.audience_inactiveSinceDays} onChange={handleChange} placeholder="e.g. 30" />
                    </div>
                    <div>
                      <Label sub="Users with NO booking of ANY status (including Cancelled) in last N days">Inactive Since (Any Status)</Label>
                      <Input name="audience_inactiveSinceDaysAnyStatus" type="number" min="0" value={form.audience_inactiveSinceDaysAnyStatus} onChange={handleChange} placeholder="e.g. 30" />
                    </div>
                    <div>
                      <Label sub="Users with a booking within last N days (active)">Last Active Within (days)</Label>
                      <Input name="audience_lastActiveDays" type="number" min="0" value={form.audience_lastActiveDays} onChange={handleChange} placeholder="e.g. 90" />
                    </div>
                    <div>
                      <Label sub="Users whose booking was set to VERIFIED or PAYMENT_VERIFICATION_PENDING within the last N hours (uses verified_at timestamp)">Verified In Last (hours)</Label>
                      <Input name="audience_verifiedInLastHours" type="number" min="1" value={form.audience_verifiedInLastHours} onChange={handleChange} placeholder="e.g. 24" />
                    </div>
                  </div>
                </div>

                {/* Special Occasions */}
                <div className="border rounded-lg p-4">
                  <SectionHeader icon="🎂" title="Special Occasions" desc="For birthday campaigns" />
                  <div>
                    <Label sub="Send to users whose birthday is exactly N days from today">Days Before Birthday</Label>
                    <Input name="audience_daysBeforeBirthday" type="number" min="0" max="30" value={form.audience_daysBeforeBirthday} onChange={handleChange} placeholder="e.g. 7 (1 week before birthday)" />
                  </div>
                </div>

                {/* Live Preview */}
                {audiencePreview && (
                  <div className="bg-gray-50 border rounded-lg p-3">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase">Audience Rules Preview</p>
                      <button 
                        type="button" 
                        onClick={() => getAudiencePreview({ variables: { audienceRules: audiencePreview }})}
                        className="bg-blue-600 text-white text-xs px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                        disabled={previewLoading}
                      >
                        {previewLoading ? "Calculating..." : "Calculate Audience Size"}
                      </button>
                    </div>
                    {previewData && previewData.previewCampaignAudience && (
                      <div className="mb-3 p-3 bg-blue-100 text-blue-800 rounded text-sm font-semibold flex justify-between items-center">
                        <span>Total Users Matching Rules:</span>
                        <span className="text-lg">{previewData.previewCampaignAudience.count} users</span>
                      </div>
                    )}
                    {previewData?.previewCampaignAudience?.error && (
                      <div className="mb-3 p-2 bg-red-100 text-red-700 text-xs rounded">
                        Error: {previewData.previewCampaignAudience.error}
                      </div>
                    )}
                    <pre className="text-xs text-green-700 bg-gray-900 rounded p-3 overflow-x-auto">
                      {JSON.stringify(audiencePreview, null, 2)}
                    </pre>
                  </div>
                )}
              </>
            )}

            {/* ─── STEP 3: Communication + Metadata ─── */}
            {step === 3 && (
              <>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-700">
                  <strong>Step 3 of 3:</strong> Set up the communication channel and trigger/schedule config.
                </div>

                {/* Channel */}
                <div className="border rounded-lg p-4 space-y-4">
                  <SectionHeader icon="📱" title="Communication Channel" />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Channel</Label>
                      <Select name="communicationChannel" value={form.communicationChannel} onChange={handleChange}>
                        <option value="WHATSAPP">WhatsApp</option>
                        <option value="SMS">SMS</option>
                        <option value="PUSH">Push Notification</option>
                        <option value="NONE">None (Incentive Only)</option>
                      </Select>
                    </div>
                    <div>
                      <Label sub="Orders within this window are attributed to this campaign">Attribution Window (hours)</Label>
                      <Input name="attributionWindowHours" type="number" value={form.attributionWindowHours} onChange={handleChange} placeholder="168" />
                    </div>
                  </div>
                </div>

                {/* Batch Release (throttling) */}
                <div className="border rounded-lg p-4 space-y-4">
                  <SectionHeader icon="🚦" title="Batch Release" desc="Only applies to campaigns with steps (Journey Engine). Drip-feed a large audience instead of activating everyone at once." />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label sub="Max users activated per interval. Leave blank to activate the whole audience immediately.">Batch Size</Label>
                      <Input name="batchSize" type="number" min="1" value={form.batchSize} onChange={handleChange} placeholder="e.g. 2000" />
                    </div>
                    <div>
                      <Label sub="Hours to wait before releasing the next batch">Batch Interval (hours)</Label>
                      <Input name="batchIntervalHours" type="number" min="1" value={form.batchIntervalHours} onChange={handleChange} placeholder="e.g. 24" />
                    </div>
                  </div>
                  {form.batchSize && (
                    <p className="text-xs text-blue-600 bg-blue-50 rounded px-2 py-1">
                      ℹ️ e.g. with 16,000 eligible users, {form.batchSize} per batch, every {form.batchIntervalHours || "?"}h → releases over {Math.ceil(16000 / (parseInt(form.batchSize) || 1))} batches.
                    </p>
                  )}
                </div>

                {/* Schedule / Event config */}
                <div className="border rounded-lg p-4 space-y-4">
                  <SectionHeader icon="⚙️" title="Trigger Configuration" desc="Required for Cron Scheduled or Event Driven campaigns" />
                  {form.triggerType === "CRON_SCHEDULED" && (
                    <div>
                      <Label sub="Standard cron expression (e.g. '0 10 * * *' = daily at 10am)">Cron Schedule</Label>
                      <Input name="meta_cronSchedule" value={form.meta_cronSchedule} onChange={handleChange} placeholder="0 10 * * *" />
                    </div>
                  )}
                  {form.triggerType === "EVENT_DRIVEN" && (
                    <div>
                      <Label>Trigger Event</Label>
                      <Select name="meta_triggerEvent" value={form.meta_triggerEvent} onChange={handleChange}>
                        {TRIGGER_EVENTS.map((e) => (
                          <option key={e.value} value={e.value}>{e.label}</option>
                        ))}
                      </Select>
                    </div>
                  )}
                </div>

                {/* Summary */}
                <div className="bg-gray-50 border rounded-lg p-4 text-sm space-y-1 text-gray-600">
                  <p className="font-semibold text-gray-800 mb-2">Campaign Summary</p>
                  <p><span className="font-medium">Name:</span> {form.name || "—"}</p>
                  <p><span className="font-medium">Type:</span> {form.campaignType || "—"}</p>
                  <p><span className="font-medium">Trigger:</span> {form.triggerType}</p>
                  <p><span className="font-medium">Channel:</span> {form.communicationChannel}</p>
                  {audiencePreview && (
                    <p><span className="font-medium">Audience Filters:</span> {Object.keys(audiencePreview).join(", ")}</p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-between flex-shrink-0">
            <button
              type="button"
              onClick={step === 1 ? onClose : () => setStep(step - 1)}
              className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100 transition"
            >
              {step === 1 ? "Cancel" : "← Back"}
            </button>
            {step < 3 ? (
              <button
                key="next"
                type="button"
                onClick={() => {
                  if (step === 1 && !validate()) return;
                  setStep(step + 1);
                }}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
              >
                Next →
              </button>
            ) : (
              <button
                key="submit"
                type="submit"
                className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition font-medium"
              >
                {initialData ? "Save Changes" : "Create Campaign"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCampaignModal;
